import { useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { FEATURES } from '../../features';
import SidebarDashboardIcon from '../icons/SidebarDashboardIcon';
import SidebarContractorsIcon from '../icons/SidebarContractorsIcon';
import {
  FaBuilding,
  FaUsers,
  FaTools,
  FaPoundSign,
  FaChartLine,
  FaUserTie,
  FaCog,
  FaLink,
  FaArchive,
  FaShieldAlt,
  FaBullhorn,
  FaClipboardCheck,
  FaFileAlt,
  FaInbox,
  FaAddressBook,
  FaTag,
  FaCalendarAlt,
  FaFileSignature,
} from 'react-icons/fa';

export const navMenuItems = [
  {
    title: 'Dashboard',
    path: '/dashboard',
    icon: SidebarDashboardIcon,
    pageName: 'dashboard',
    requiredFeature: 'viewDashboard',
  },
  {
    title: 'Properties',
    path: '/properties',
    icon: FaBuilding,
    pageName: 'properties',
    requiredFeature: 'viewProperties',
  },
  {
    title: 'Sales',
    path: '/sales',
    icon: FaTag,
    pageName: 'sales',
    requiredFeature: 'viewProperties',
  },
  {
    title: 'Tenants',
    path: '/tenants',
    icon: FaUsers,
    pageName: 'tenants',
    requiredFeature: 'viewTenants',
  },
  {
    title: 'Landlords',
    path: '/landlords',
    icon: FaUserTie,
    pageName: 'landlords',
    requiredFeature: 'viewLandlords',
  },
  {
    title: 'Enquiries',
    path: '/enquiries',
    icon: FaInbox,
    pageName: 'enquiries',
    requiredFeature: null,
  },
  {
    title: 'Bookings',
    path: '/bookings',
    icon: FaCalendarAlt,
    pageName: 'enquiries',
    requiredFeature: null,
  },
  {
    title: 'Compliance',
    path: '/compliance',
    icon: FaShieldAlt,
    pageName: 'properties',
    requiredFeature: 'viewProperties',
  },
  {
    title: 'Maintenance',
    path: '/maintenance',
    icon: FaTools,
    pageName: 'maintenance',
    requiredFeature: 'viewMaintenance',
  },
  {
    title: 'Inspections',
    shortTitle: 'Inspections',
    path: '/inspections',
    icon: FaClipboardCheck,
    pageName: 'inspections',
    requiredFeature: 'viewInspections',
  },
  {
    title: 'Inspection Report Builder',
    shortTitle: 'Report Builder',
    path: '/inspection-report-builder',
    icon: FaFileSignature,
    pageName: 'inspectionReportBuilder',
    requiredFeature: 'viewInspections',
  },
  {
    title: 'Finance',
    path: '/finance',
    icon: FaPoundSign,
    pageName: 'finance',
    requiredFeature: 'viewFinance',
  },
  {
    title: 'Reports',
    path: '/reports',
    icon: FaChartLine,
    pageName: 'reports',
    requiredFeature: 'viewReports',
  },
  {
    title: 'Contractors',
    path: '/contractors',
    icon: SidebarContractorsIcon,
    pageName: 'contractors',
    requiredFeature: 'viewContractors',
  },
  {
    title: 'Contacts',
    path: '/contacts',
    icon: FaAddressBook,
    pageName: 'properties',
    requiredFeature: 'viewProperties',
  },
  {
    title: 'Links',
    path: '/links',
    icon: FaLink,
    pageName: 'links',
    requiredFeature: 'viewLinks',
  },
  {
    title: 'Company Docs',
    shortTitle: 'Docs',
    path: '/company-documents',
    icon: FaFileAlt,
    pageName: 'companyDocuments',
    requiredFeature: 'viewDocuments',
  },
  {
    title: 'Broadcast',
    path: '/broadcast',
    icon: FaBullhorn,
    pageName: 'broadcast',
    requiredFeature: 'sendBroadcasts',
  },
  {
    title: 'Archive',
    path: '/archive',
    icon: FaArchive,
    pageName: 'archive',
    requiredFeature: 'viewArchive',
    adminOnly: true,
  },
  ...(process.env.REACT_APP_COMPANY_NAME === 'tog-plutoo'
    ? [{
        title: 'Schedule D',
        path: '/schedule-d',
        pageName: 'scheduleD',
        requiredFeature: 'viewScheduleD',
      }]
    : []),
  {
    title: 'Settings',
    path: '/settings',
    icon: FaCog,
    pageName: 'settings',
    requiredFeature: 'accessSettings',
  },
  {
    title: 'Components',
    path: '/components',
    icon: FaFileAlt,
    pageName: 'components',
    requiredFeature: null,
  },
];

// ─── Grouped nav config ───────────────────────────────────────────────────────
// Defines which items are top-level direct links vs. nested under a dropdown.
export const NAV_GROUP_CONFIG = [
  { type: 'link', path: '/dashboard' },
  { type: 'link', path: '/properties' },
  { type: 'link', path: '/sales' },
  {
    type: 'group',
    title: 'People',
    paths: ['/tenants', '/landlords', '/contractors', '/contacts'],
  },
  { type: 'link', path: '/enquiries' },
  { type: 'link', path: '/bookings' },
  { type: 'link', path: '/maintenance' },
  { type: 'link', path: '/compliance' },
  {
    type: 'group',
    title: 'Operations',
    paths: [
      '/inspections',
      '/inspection-report-builder',
      '/finance',
      '/reports',
      '/broadcast',
      '/links',
      '/company-documents',
      '/archive',
      ...(process.env.REACT_APP_COMPANY_NAME === 'tog-plutoo'
        ? ['/schedule-d']
        : []),
    ],
  },
  { type: 'link', path: '/settings' },
  { type: 'link', path: '/components' },
];

// Desktop sidebar sections (Settings lives on the user card, not in a section).
export const SIDEBAR_GROUP_CONFIG = [
  { title: 'Work', paths: ['/dashboard', '/enquiries', '/bookings', '/inspections'] },
  { title: 'Portfolio', paths: ['/properties', '/sales', '/compliance', '/maintenance'] },
  { title: 'People', paths: ['/tenants', '/landlords', '/contractors', '/contacts'] },
  {
    title: 'Operations',
    paths: ['/finance', '/reports', '/inspection-report-builder', '/broadcast', '/links', '/company-documents', '/archive', '/schedule-d', '/components'],
  },
];

export const useFilteredNavGroups = () => {
  const filteredItems = useFilteredNavMenuItems();

  return useMemo(() => {
    const itemByPath = new Map(filteredItems.map((item) => [item.path, item]));
    const result = [];

    for (const config of NAV_GROUP_CONFIG) {
      if (config.type === 'link') {
        const item = itemByPath.get(config.path);
        if (item) result.push({ type: 'link', item });
      } else {
        const children = config.paths.map((p) => itemByPath.get(p)).filter(Boolean);
        if (children.length > 0) {
          result.push({ type: 'group', title: config.title, children });
        }
      }
    }
    return result;
  }, [filteredItems]);
};

// ─────────────────────────────────────────────────────────────────────────────

export const useFilteredNavMenuItems = () => {
  const { userRole, userPermissions, currentUser } = useAuth();
  const { isEnabled: isFeatureEnabled, hubFlags, localFlags } = useFeatureFlags();

  return useMemo(() => {
    if (!userPermissions) return [];

    const normalizedUserRole = userRole?.toLowerCase();
    const isAdminOrOwner =
      normalizedUserRole === 'administrator' || normalizedUserRole === 'owner';
    const normalizedEmail = currentUser?.email?.toLowerCase();
    const emailMatches = (item) =>
      Array.isArray(item.emailOnly) &&
      item.emailOnly.map((e) => e.toLowerCase()).includes(normalizedEmail);

    return navMenuItems.filter((item) => {
      // Email-gated items: shown ONLY to allow-listed users, bypassing role/permission checks.
      if (Array.isArray(item.emailOnly)) {
        return emailMatches(item);
      }

      if (!isAdminOrOwner) {
        const hasPageAccess = userPermissions?.pages?.[item.pageName] === true;
        const hasFeatureAccess = item.requiredFeature
          ? userPermissions?.features?.[item.requiredFeature] === true
          : false;
        // Show if either page access or required feature is enabled
        if (!hasPageAccess && !hasFeatureAccess) return false;
      }

      if (item.adminOnly === true) {
        const isAdministrator =
          normalizedUserRole === 'administrator' || normalizedUserRole === 'admin';
        if (!isAdministrator) return false;
      }

      if (item.path === '/finance' && !isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        return false;
      }

      if (item.path === '/reports' && !isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        return false;
      }

      if (item.path === '/inspections' && !isFeatureEnabled(FEATURES.INSPECTIONS)) {
        return false;
      }

      if (item.path === '/inspection-report-builder'
        && !isFeatureEnabled(FEATURES.INSPECTION_REPORT_BUILDER)) {
        return false;
      }

      return true;
    });
  }, [userPermissions, userRole, currentUser, hubFlags, localFlags, isFeatureEnabled]);
};

export const isNavItemActive = (path, locationPathname) => {
  if (path === '/dashboard') {
    return locationPathname === '/dashboard' || locationPathname === '/';
  }
  return locationPathname.startsWith(path);
};
